import type { Project, Subproject } from "@org-avp/avp-avengers-project-service-api";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectCurrentProject, selectCurrentSubproject } from "../slices/projectSlice";

export function useProjectParam<K extends keyof Project, T extends Project[K] & string>(param: K): T | null | undefined;
export function useProjectParam<K extends keyof Project>(param: K, mapper: (urlParam: string) => Project[K]): Project[K] | null | undefined;
export function useProjectParam<K extends keyof Project>(
  param: K,
  mapper: (urlParam: string) => Project[K] = (it) => it as Project[K],
): Project[K] | null | undefined {
  const project = useSelector(selectCurrentProject);
  const urlParam = useParams()[param];
  return (urlParam ? mapper(urlParam) : null)
    ?? project?.[param];
}

export function useRequiredProjectParam<K extends keyof Project, T extends Project[K] & string>(param: K): T;
export function useRequiredProjectParam<K extends keyof Project>(param: K, mapper: (urlParam: string) => Project[K]): Project[K];
export function useRequiredProjectParam<K extends keyof Project>(
  param: K,
  mapper: (urlParam: string) => Project[K] = (it) => it as Project[K],
): Project[K] {
  const value = useProjectParam(param, mapper);
  if (value !== null && value !== undefined) {
    return value;
  }

  throw new Error("project param " + param + " not defined.");
}

export function useSubprojectParam<K extends keyof Subproject, T extends Subproject[K] & string>(param: K): T | null | undefined;
export function useSubprojectParam<K extends keyof Subproject>(param: K, mapper: (urlParam: string) => Subproject[K]): Subproject[K] | null | undefined;
export function useSubprojectParam<K extends keyof Subproject>(
  param: K,
  mapper: (urlParam: string) => Subproject[K] = (it) => it as Subproject[K],
): Subproject[K] | null | undefined {
  const subproject = useSelector(selectCurrentSubproject);
  const urlParam = useParams()[param];
  return (urlParam ? mapper(urlParam) : null)
    ?? subproject?.[param];
}

export function useRequiredSubprojectParam<K extends keyof Subproject, T extends Subproject[K] & string>(param: K): T;
export function useRequiredSubprojectParam<K extends keyof Subproject>(param: K, mapper: (urlParam: string) => Subproject[K]): Subproject[K];
export function useRequiredSubprojectParam<K extends keyof Subproject>(
  param: K,
  mapper: (urlParam: string) => Subproject[K] = (it) => it as Subproject[K],
): Subproject[K] {
  const value = useSubprojectParam(param, mapper);
  if (value !== null && value !== undefined) {
    return value;
  }

  throw new Error("subproject param " + param + " not defined.");
}
